import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import 'prop-types';
import { darken, rgba } from 'polished';
import styled from 'styled-components';
import { getDropShadowByElevation } from '../../styles/elevation.js';

var base = {
  alignSelf: "flex-start",
  background: "none",
  borderStyle: "none",
  borderWidth: 0,
  cursor: "pointer",
  display: "inline-block",
  fontWeight: 600,
  letterSpacing: "inherit",
  maxWidth: "100%",
  outline: "none",
  padding: 0,
  position: "relative",
  userSelect: "none",
  WebkitTapHighlightColor: "transparent",
  whiteSpace: "nowrap"
};
function getColorsByVariant() {
  var variant = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "secondary";
  var theme = arguments.length > 1 ? arguments[1] : undefined;
  var error = theme.alertColors.error,
      _theme$CTAColors = theme.CTAColors,
      primary = _theme$CTAColors.primary,
      primaryVariant = _theme$CTAColors.primaryVariant,
      _theme$neutralColors = theme.neutralColors,
      charcoal = _theme$neutralColors.charcoal,
      smoke = _theme$neutralColors.smoke,
      silver = _theme$neutralColors.silver,
      white = _theme$neutralColors.white;
  var errorVariant = darken(0.1, error);
  var ghostHoverBgColor = rgba(silver, 0.4);
  var colorsByVariant = {
    primary: {
      bgColor: primary,
      bgColorActive: void 0,
      bgColorHover: primaryVariant,
      borderColor: primary,
      borderColorHover: primaryVariant,
      borderColorActive: void 0,
      color: white,
      colorActive: void 0,
      colorHover: white,
      shadow: void 0,
      shadowHover: void 0
    },
    secondary: {
      bgColor: white,
      bgColorActive: void 0,
      bgColorHover: white,
      borderColor: silver,
      borderColorHover: primaryVariant,
      borderColorActive: void 0,
      color: primary,
      colorActive: void 0,
      colorHover: primaryVariant,
      shadow: void 0,
      shadowHover: void 0
    },
    tertiary: {
      bgColor: white,
      bgColorActive: void 0,
      bgColorHover: void 0,
      borderColor: silver,
      borderColorHover: charcoal,
      borderColorActive: void 0,
      color: charcoal,
      colorActive: void 0,
      colorHover: void 0,
      shadow: void 0,
      shadowHover: void 0
    },
    ghost: {
      bgColor: void 0,
      bgColorActive: ghostHoverBgColor,
      bgColorHover: ghostHoverBgColor,
      borderColor: void 0,
      borderColorHover: void 0,
      borderColorActive: void 0,
      color: smoke,
      colorActive: void 0,
      colorHover: void 0,
      shadow: void 0,
      shadowHover: void 0
    },
    warning: {
      bgColor: error,
      bgColorActive: errorVariant,
      bgColorHover: errorVariant,
      borderColor: error,
      borderColorHover: errorVariant,
      borderColorActive: charcoal,
      color: white,
      colorActive: void 0,
      colorHover: void 0,
      shadow: void 0,
      shadowHover: void 0
    }
  };
  return colorsByVariant[variant] || colorsByVariant.secondary;
}

function getStylesByVariant(variant, theme) {
  var _getColorsByVariant = getColorsByVariant(variant, theme),
      bgColor = _getColorsByVariant.bgColor,
      bgColorActive = _getColorsByVariant.bgColorActive,
      bgColorHover = _getColorsByVariant.bgColorHover,
      borderColor = _getColorsByVariant.borderColor,
      borderColorActive = _getColorsByVariant.borderColorActive,
      borderColorHover = _getColorsByVariant.borderColorHover,
      color = _getColorsByVariant.color,
      colorActive = _getColorsByVariant.colorActive,
      colorHover = _getColorsByVariant.colorHover;

  var hasNoActiveState = !bgColorActive && !borderColorActive && !colorActive;
  var styles = {
    backgroundColor: bgColor,
    borderColor: borderColor || "transparent",
    color: color
  };

  if (hasNoActiveState) {
    styles[":hover, :focus, :active, &.mntlui-active"] = {
      backgroundColor: bgColorHover,
      borderColor: borderColorHover || "transparent",
      color: colorHover,
      textDecoration: "none",
      transition: "background-color 0.25s, border-color 0.25s"
    };
  } else {
    styles[":hover, :focus"] = {
      backgroundColor: bgColorHover,
      borderColor: borderColorHover || "transparent",
      color: colorHover,
      textDecoration: "none",
      transition: "background-color 0.25s, border-color 0.25s, color 0.25s"
    };
    styles[":active, &.mntlui-active"] = {
      backgroundColor: bgColorActive,
      borderColor: borderColorActive || "transparent",
      color: colorActive,
      textDecoration: "none",
      transition: "border-color 0.25s, color 0.25s"
    };
  }

  return styles;
}

var StyledButtonBase = styled.button.withConfig({
  displayName: "StyledButton__StyledButtonBase",
  componentId: "sc-1fmfard-0"
})(function (_ref) {
  var theme = _ref.theme;
  var _theme$neutralColors2 = theme.neutralColors,
      rhino = _theme$neutralColors2.rhino,
      silver = _theme$neutralColors2.silver,
      white = _theme$neutralColors2.white;
  var boxShadow = getDropShadowByElevation(1);
  var boxShadowHover = getDropShadowByElevation(2);
  return {
    "&&": _objectSpread(_objectSpread({}, base), {}, {
      borderColor: "transparent",
      borderRadius: 4,
      borderStyle: "solid",
      borderWidth: 1,
      fontSize: 14,
      lineHeight: "16px",
      minHeight: 32,
      minWidth: 32,
      padding: "7px 15px"
    }),
    "&.mntlui-hideLabel": {
      padding: "7px 7px"
    },
    "&.mntlui-elevated": {
      boxShadow: boxShadow,
      ":hover": {
        boxShadow: boxShadowHover
      }
    },
    "&&.mntlui-disabled": {
      backgroundColor: white,
      color: rhino,
      cursor: "not-allowed",
      ":hover, :active, :focus": {
        color: rhino,
        outline: "none",
        backgroundColor: white
      },
      "&.mntlui-secondary, &.mntlui-tertiary": {
        borderColor: silver
      },
      "&.mntlui-primary, &.mntlui-warning": {
        ":hover": {
          backgroundColor: silver
        },
        backgroundColor: silver,
        borderColor: "transparent"
      },
      "&.mntlui-elevated": {
        ":hover": {
          boxShadow: boxShadow
        }
      }
    }
  };
});
var StyledButton = styled(StyledButtonBase).withConfig({
  displayName: "StyledButton",
  componentId: "sc-1fmfard-1"
})(function (_ref2) {
  var theme = _ref2.theme,
      variant = _ref2.variant;
  return _defineProperty({}, "&.mntlui-".concat(variant), getStylesByVariant(variant, theme));
});

export { StyledButton, base, getColorsByVariant };
